<script lang="ts" setup>
import { BasicButton } from '~/types';
import { ref } from 'vue';
import { A11y } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Swiper as SwiperClass, SwiperModule } from 'swiper/types';
import { Icon } from '@iconify/vue';
import Container from '~/components/Container.vue';
import EmbedHtml from '~/components/Common/EmbedHtml.vue';
import TilesSliderItem, { Item } from './TilesSlider/Item.vue';
import ExternalLinkWarning from '~/components/ui/ExternalLinkWarning.vue';

interface Props {
  anchor?: string;
  title?: string;
  subtitle?: string;
  buttons?: BasicButton[];
  description?: string;
  items: Item[];
}

defineProps<Props>();

const modules: SwiperModule[] = [A11y];
const swiperUse = ref<SwiperClass | null>(null);

function onSwiper(swiper: SwiperClass) {
  swiperUse.value = swiper;
}

const swiperOptions = {
  init: false,
  wrapperTag: 'div',
  modules,
  slidesPerView: 'auto',
  keyboard: {
    enabled: true,
    onlyInViewport: false,
  },
};
</script>

<template>
  <section v-if="items && items.length > 0" :id="anchor" class="tiles-slider">
    <Container class="tiles-slider__container">
      <div v-if="title || subtitle" class="tiles-slider__heading">
        <h2 v-if="title" class="tiles-slider__title">{{ title }}</h2>
        <p v-if="subtitle" class="tiles-slider__subtitle">{{ subtitle }}</p>
      </div>
      <div class="tiles-slider__slider-wrapper">
        <Swiper v-bind="swiperOptions" @swiper="onSwiper">
          <SwiperSlide v-for="(item, index) in items" :key="`tiles-slider-item-${index}`">
            <TilesSliderItem :item="item" />
          </SwiperSlide>
        </Swiper>
        <button
          class="swiper-button-prev"
          :class="{
            'swiper-button-disabled': swiperUse?.isBeginning,
            'swiper-button-lock': swiperUse?.isLocked,
          }"
          @click="swiperUse?.slidePrev()"
        >
          <span class="sr-only">Prev Slide</span>
          <Icon icon="bi:arrow-left" />
        </button>
        <button
          class="swiper-button-next"
          :class="{
            'swiper-button-disabled': swiperUse?.isEnd,
            'swiper-button-lock': swiperUse?.isLocked,
          }"
          @click="swiperUse?.slideNext()"
        >
          <span class="sr-only">Next Slide</span>
          <Icon icon="bi:arrow-right" />
        </button>
      </div>
      <EmbedHtml v-if="description" :content="description" class="tiles-item__description" />
      <div v-if="buttons && buttons.length > 0" class="tiles-slider__buttons">
        <a
          v-for="(button, index) in buttons"
          :key="`tiles-slider-button-${index}`"
          :href="button.url"
          :target="button.target || '_blank'"
          class="tiles-slider__button"
        >
          {{ button.label }}
          <ExternalLinkWarning :target="button.target" />
        </a>
      </div>
    </Container>
  </section>
</template>

<style lang="scss" scoped>
.tiles-slider {
  @apply pt-10 pb-16 bg-dark overflow-hidden;
  @apply md:pt-20 md:pb-24;
  @apply xl:pt-24 xl:pb-28;

  &__container {
    @apply flex flex-col gap-8;
    @apply xl:gap-16;
  }

  &__heading {
    @apply flex flex-col items-center text-center gap-6;
    @apply xl:gap-10;
  }

  &__title {
    @apply text-white font-semibold text-[24px] leading-[1.1];
    @apply md:text-[30px];
    @apply xl:text-[38px];
  }

  &__subtitle {
    @apply text-white/70 leading-[1.3] text-[16px];
    @apply xl:text-[20px];
  }

  &__buttons {
    @apply flex flex-wrap justify-center items-center gap-x-6 gap-y-4;
  }

  &__button {
    @apply flex items-center justify-center px-6 py-3 font-semibold uppercase text-[16px] rounded-full bg-accent text-white duration-300 hover:bg-white hover:text-accent;
    @apply xl:min-w-[240px] xl:py-4;
  }

  &__slider-wrapper {
    @apply flex flex-wrap justify-center;
  }

  .swiper {
    @apply w-[calc(100%+1.5rem)] overflow-visible;

    &-button-prev,
    &-button-next {
      @apply static text-white after:hidden translate-y-0 mt-10 mx-1 h-auto w-auto p-2 [&>svg]:w-10 duration-300 hover:text-light-accent;
    }
  }

  :deep(.swiper-slide) {
    @apply px-3 w-full first:ml-auto last:mr-auto;
    @apply md:w-1/2;
    @apply xl:w-1/3;
    @apply 2xl:w-[calc(100%/3.5)];
  }

  :deep(.text-container > *) {
    @apply text-white font-medium text-[18px] leading-[1.3];
    @apply xl:text-[24px];
  }
}
</style>
